import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CatalogAvailability,
  CatalogItem,
  CatalogItemResponse,
  CatalogMetadata,
  EvaluateRefQualifierRequest,
  EvaluateRefQualifierResponse,
  GlideAjaxRequest,
  GlideAjaxResponse,
  ReferenceLookupRecord,
  ReferenceLookupResponse,
  ReferenceOption,
} from '../models/catalog-form';

// Client sends only content-negotiation headers. `client_id`, `client_secret`
// and `X-User` are injected server-side — by proxy.conf.js `/r2f` in local dev,
// by the AWS Lambda in deployed envs. No secret ever ships in the SPA bundle.
@Injectable({ providedIn: 'root' })
export class CatalogFormService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = environment.aws.fulfillmentEndpoint;

  // Cached once — HttpHeaders is immutable, so a shared instance is safe.
  // The old per-call getter allocated a new object on every request.
  private readonly _headers = new HttpHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });

  // Mulesoft doc STEP 0 — call before loading item/metadata to confirm the
  // caller (or a specified `user`) is entitled to view and submit this
  // catalog item. `available===false` means the SPA should short-circuit
  // and render the `reasons` array instead of fetching the form skeleton.
  // `user` accepts a sys_id or an email; omit to check the calling user.
  checkAvailability(
    sysId: string,
    user?: string,
  ): Observable<CatalogAvailability> {
    let params = new HttpParams();
    if (user) params = params.set('user', user);
    return this._http.get<CatalogAvailability>(
      `${this._apiUrl}/catalog/items/${sysId}/availability`,
      { headers: this._headers, params },
    );
  }

  // The /catalog/items/{sysId} response wraps the item under `result`. Unwrap
  // here so callers see a plain CatalogItem. The metadata response is NOT
  // wrapped — see loadCatalogMetadata.
  loadCatalogItem(sysId: string): Observable<CatalogItem> {
    return this._http
      .get<CatalogItemResponse>(this._itemUrl(sysId), { headers: this._headers })
      .pipe(map((res) => res.result));
  }

  // r2f 1.1.3: `user` (email or SN sys_id — never the login ID) decides for
  // whom user-scoped defaults resolve into `defaultValueEvaluated`. Omitted,
  // Mule resolves the X-User login with an extra SN call. An email SN does
  // not know silently resolves to the integration account.
  loadCatalogMetadata(sysId: string, user?: string): Observable<CatalogMetadata> {
    let params = new HttpParams();
    if (user) params = params.set('user', user);
    return this._http.get<CatalogMetadata>(this._metadataUrl(sysId), {
      headers: this._headers,
      params,
    });
  }

  // Guide §2.3: GET params are `table`, `search`, `limit`, optional `qualifier`
  // (encoded SN query string, resolved from a javascript: refQualifier). The
  // Mulesoft response is camelCase (`sysId`, `displayValue`); we normalise to
  // snake_case here so the renderer (and the future GFormMock) never has to
  // branch on wire format.
  referenceLookup(
    table: string,
    search: string,
    limit = 10,
    qualifier?: string,
    searchExtra = false,
    fields?: string,
    displayField?: string,
  ): Observable<ReferenceOption[]> {
    let params = new HttpParams()
      .set('table', table)
      .set('search', search)
      .set('limit', String(limit));
    if (qualifier) {
      params = params.set('qualifier', qualifier);
    }
    if (searchExtra === true) {
      // Mulesoft doc: only send searchExtra when the field's
      // refAcColumnsSearch attribute is set to `"true"` — SN's default
      // behaviour matches when the param is omitted entirely.
      params = params.set('searchExtra', 'true');
    }
    if (displayField) {
      // r2f 1.1.3 §11: the column `search` matches (STARTSWITH) and sorts on.
      // Derived from the variable's `ref_ac_order_by` — never `ed.searchField`,
      // which is always "name" and wrong for tables like u_st_ai_use_cases.
      params = params.set('displayField', displayField);
    }
    if (fields) {
      // Comma-separated extra column names Mulesoft should echo per record
      // (derived from the field's `refAcColumns` metadata attribute; guide
      // §5.6). SN uses these to show a multi-column autocomplete dropdown.
      params = params.set('fields', fields);
    }
    return this._http
      .get<ReferenceLookupResponse>(`${this._apiUrl}/catalog/referenceLookup`, {
        headers: this._headers,
        params,
      })
      .pipe(map((res) => (res?.records ?? []).map(normaliseReferenceRecord)));
  }

  // Guide §2.4: server-side evaluation of a `javascript:` refQualifier.
  // `resolvedQuery` is the encoded SN query string downstream `referenceLookup`
  // calls should pass as `qualifier`. `records` is the first page of matching
  // rows the server evaluated alongside the qualifier — Mulesoft's own guidance
  // says to seed the field's typeahead with these and skip the extra
  // `referenceLookup` round-trip on load. If the response is missing
  // `resolvedQuery` we log and return `''` + no records. r2f 1.1.3 fails
  // closed: `advanced` / `dynamic` qualifiers come back with empty `records`
  // and an explanation in `warnings[]` — callers must NOT fall back to an
  // unfiltered referenceLookup.
  evaluateRefQualifier(
    body: EvaluateRefQualifierRequest,
  ): Observable<{ resolvedQuery: string; records: ReferenceOption[] }> {
    return this._http
      .post<EvaluateRefQualifierResponse>(
        `${this._apiUrl}/catalog/evaluateRefQualifier`,
        body,
        { headers: this._headers },
      )
      .pipe(
        map((res) => {
          const records = (res?.records ?? []).map(normaliseReferenceRecord);
          if (res && typeof res.resolvedQuery === 'string') {
            return { resolvedQuery: res.resolvedQuery, records };
          }
          console.info(
            '[CatalogFormService] evaluateRefQualifier returned no `resolvedQuery`; field has no options.',
            res?.warnings ?? res,
          );
          return { resolvedQuery: '', records };
        }),
      );
  }

  // Guide §2.5: bridge for GlideAjax script-include calls. Response shape
  // varies per script include (raw string, JSON, XML answer) so the wire
  // type is kept permissive.
  executeGlideAjax(body: GlideAjaxRequest): Observable<GlideAjaxResponse> {
    return this._http.post<GlideAjaxResponse>(
      `${this._apiUrl}/catalog/executeGlideAjax`,
      body,
      { headers: this._headers },
    );
  }

  private _itemUrl(sysId: string): string {
    return `${this._apiUrl}/catalog/items/${sysId}`;
  }

  private _metadataUrl(sysId: string): string {
    return `${this._apiUrl}/catalog/items/${sysId}/metadata`;
  }
}

function normaliseReferenceRecord(r: ReferenceLookupRecord): ReferenceOption {
  const option: ReferenceOption = {
    sys_id: r.sysId ?? '',
    display_value: r.displayValue ?? '',
  };
  const extras: Record<string, string> = {};
  for (const [key, value] of Object.entries(r)) {
    if (key === 'sysId' || key === 'displayValue') continue;
    if (typeof value === 'string') extras[key] = value;
  }
  if (Object.keys(extras).length > 0) {
    option.columns = extras;
  }
  return option;
}
